import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import {
  buildGuaranteesApplicationAmApprovalLetterPath,
  buildGuaranteesApplicationAmApprovalLetterManualSignPath,
  getStringValue,
} from '~/modules/guarantees/utils.ts';
import { isGuaranteesApprovalLetterPaperExecutionCheck } from '../../shared/approvalLetterExecution.ts';
import { useGuaranteesPdfPreview } from '../../shared/hooks/useGuaranteesPdfPreview.ts';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import { getCurrentRouteGuaranteesApplication } from '../../shared/utils/applicationIdentity.ts';
import { useGuaranteesApplicationFilesSync } from '../shared/SidePanel/applicationFilesSync.ts';
import {
  getClientApprovalLetterFile,
  getClientApprovalLetterPreviewFileId,
} from '../../client/step3/utils.ts';
import {
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
  selectGuaranteesManagerSupervisorInfoStatus,
  selectGuaranteesSubmitApprovalLetterByAmManagerError,
  selectGuaranteesSubmitApprovalLetterByAmManagerStatus,
  selectHasGuaranteesAmAccess,
} from '~/store/guarantees/selectors.ts';
import {
  downloadGuaranteesClientDocument,
  fetchGuaranteesApplication,
  fetchGuaranteesApplicationFiles,
  submitGuaranteesApprovalLetterByAmManager,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  GuaranteesApplicationStatus,
  GuaranteesFetchStatus,
  type TGuaranteesApplicationFile,
} from '~/store/guarantees/types.ts';
import { normalizeGuaranteesApplicationStatus } from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import { MANAGER_APPROVAL_LETTER_TEXT } from '../step4/constants.ts';
import { isManagerApprovalLetterManualSignStatus } from './utils.ts';
import {
  MANAGER_APPROVAL_LETTER_CLIENT_SIGNED_TEXT,
  MANAGER_APPROVAL_LETTER_MANUAL_SIGN_ACCESS_DENIED_TEXT,
  MANAGER_APPROVAL_LETTER_MANUAL_SIGN_GET_STARTED_PATH,
  MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TASKS_PATH,
} from './constants.ts';

type TPreviewDocument = {
  data: Blob;
  contentType?: string;
};

export const useManagerApprovalLetterClientSignedPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const managerSupervisorInfoStatus = useSelector(selectGuaranteesManagerSupervisorInfoStatus);
  const hasAmAccess = useSelector(selectHasGuaranteesAmAccess);
  const submitApprovalLetterByAmManagerStatus = useSelector(
    selectGuaranteesSubmitApprovalLetterByAmManagerStatus
  );
  const submitApprovalLetterByAmManagerError = useSelector(
    selectGuaranteesSubmitApprovalLetterByAmManagerError
  );
  const [applicationFiles, setApplicationFiles] = useState<TGuaranteesApplicationFile[]>([]);
  const [isApplicationFilesLoading, setIsApplicationFilesLoading] = useState(false);
  const [hasApplicationFilesLoaded, setHasApplicationFilesLoaded] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<TPreviewDocument | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const { currentRouteApplication, isLoadedApplicationCurrent } =
    getCurrentRouteGuaranteesApplication(application, applicationId);
  const effectiveApplicationId =
    getStringValue(currentRouteApplication?.applicationId || currentRouteApplication?.id) ||
    applicationId;
  const applicationNumber =
    getStringValue(currentRouteApplication?.applicationNumber) ||
    getStringValue(currentRouteApplication?.root?.applicationNumber) ||
    effectiveApplicationId;
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const isAmApprovalLetterManualSignStatus =
    isManagerApprovalLetterManualSignStatus(normalizedStatus);
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const isApplicationLoading =
    (applicationStatus === GuaranteesFetchStatus.Loading && !currentRouteApplication) ||
    (!!application && !isLoadedApplicationCurrent);
  const isAmAccessLoading =
    managerSupervisorInfoStatus === GuaranteesFetchStatus.Idle ||
    managerSupervisorInfoStatus === GuaranteesFetchStatus.Loading;
  const isAmAccessDenied = !isAmAccessLoading && !hasAmAccess;
  const currentApplicationVersion =
    typeof currentRouteApplication?.version === 'number'
      ? currentRouteApplication.version
      : undefined;
  const isSubmittingByAmManager =
    submitApprovalLetterByAmManagerStatus === GuaranteesFetchStatus.Loading;
  const clientSignedApprovalLetterFile = useMemo(
    () => getClientApprovalLetterFile(applicationFiles),
    [applicationFiles]
  );
  const clientSignedApprovalLetterPreviewFileId = getClientApprovalLetterPreviewFileId(
    clientSignedApprovalLetterFile
  );
  const isSubmitDisabled =
    currentApplicationVersion === undefined ||
    !effectiveApplicationId ||
    !clientSignedApprovalLetterPreviewFileId ||
    isSubmittingByAmManager ||
    isApplicationLoading ||
    isApplicationFilesLoading;
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const redirectPath = modalSuccess.text
    ? ''
    : !contragentId
      ? MANAGER_APPROVAL_LETTER_MANUAL_SIGN_GET_STARTED_PATH
      : currentRouteApplication && !isAmApprovalLetterManualSignStatus
        ? buildGuaranteesApplicationAmApprovalLetterPath(effectiveApplicationId, contragentId, true)
        : currentRouteApplication &&
            isGuaranteesApprovalLetterPaperExecutionCheck(currentRouteApplication)
          ? buildGuaranteesApplicationAmApprovalLetterManualSignPath(
              effectiveApplicationId,
              contragentId
            )
          : '';
  const pdfPreview = useGuaranteesPdfPreview({
    contentType: previewDocument?.contentType,
    documentBlob: previewDocument?.data,
    missingPdfText: MANAGER_APPROVAL_LETTER_CLIENT_SIGNED_TEXT.missingFileMessage,
    setModalAlert,
  });
  const isDocumentLoading =
    isApplicationFilesLoading || isPreviewLoading || pdfPreview.isDocumentLoading;
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: () => navigate(MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TASKS_PATH),
      statusConfig,
    }),
    [applicationNumber, contragentId, effectiveApplicationId, navigate, statusConfig]
  );
  const handleCloseAmAccessModal = useCallback(() => {
    handleCloseAlertModal();
    navigate(MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TASKS_PATH, { replace: true });
  }, [handleCloseAlertModal, navigate]);
  const navigateToTasks = useCallback(() => {
    navigate(MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TASKS_PATH, { replace: true });
  }, [navigate]);
  const handleSubmitByAmManager = useCallback(async () => {
    if (!effectiveApplicationId || currentApplicationVersion === undefined) {
      showGuaranteesRequestError({ message: errorMessages.SERVICE_UNAVAILABLE });

      return;
    }

    if (!clientSignedApprovalLetterPreviewFileId) {
      setModalAlert({ text: MANAGER_APPROVAL_LETTER_CLIENT_SIGNED_TEXT.missingFileMessage });

      return;
    }

    try {
      await dispatch(
        submitGuaranteesApprovalLetterByAmManager({
          applicationId: effectiveApplicationId,
          version: currentApplicationVersion,
        })
      ).unwrap();
      setModalSuccess({
        text: MANAGER_APPROVAL_LETTER_TEXT.submitSuccessMessage,
        buttonValue: MANAGER_APPROVAL_LETTER_TEXT.successCloseButton,
        onClick: navigateToTasks,
        onClose: navigateToTasks,
      });
    } catch (error) {
      showGuaranteesRequestError(error);
    }
  }, [
    clientSignedApprovalLetterPreviewFileId,
    currentApplicationVersion,
    dispatch,
    effectiveApplicationId,
    navigateToTasks,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
  ]);

  const refreshApplicationFiles = useCallback(async () => {
    if (!applicationId || applicationId === 'new') {
      setApplicationFiles([]);
      setHasApplicationFilesLoaded(true);

      return;
    }

    setIsApplicationFilesLoading(true);

    try {
      const nextApplicationFiles = await dispatch(
        fetchGuaranteesApplicationFiles({ applicationId })
      ).unwrap();

      setApplicationFiles(nextApplicationFiles);
      setHasApplicationFilesLoaded(true);
    } finally {
      setIsApplicationFilesLoading(false);
    }
  }, [applicationId, dispatch]);

  useGuaranteesApplicationFilesSync(effectiveApplicationId, () => {
    void refreshApplicationFiles().catch(showGuaranteesRequestError);
  });

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
  }, [applicationId, contragentId, dispatch]);

  useEffect(() => {
    void refreshApplicationFiles().catch(showGuaranteesRequestError);
  }, [refreshApplicationFiles, showGuaranteesRequestError]);

  useEffect(() => {
    if (isApplicationFilesLoading || !hasApplicationFilesLoaded) {
      return;
    }

    if (!clientSignedApprovalLetterPreviewFileId) {
      setPreviewDocument(null);
      setModalAlert({ text: MANAGER_APPROVAL_LETTER_CLIENT_SIGNED_TEXT.missingFileMessage });

      return;
    }

    let isCancelled = false;

    const loadPreview = async () => {
      setPreviewDocument(null);
      setIsPreviewLoading(true);

      try {
        const downloadedDocument = await dispatch(
          downloadGuaranteesClientDocument({
            id: clientSignedApprovalLetterPreviewFileId,
            isAttached: false,
          })
        ).unwrap();

        if (isCancelled) {
          return;
        }

        setPreviewDocument({
          data: downloadedDocument.data,
          contentType: downloadedDocument.contentType,
        });
      } catch (error) {
        if (!isCancelled) {
          setPreviewDocument(null);
          showGuaranteesRequestError(error);
        }
      } finally {
        if (!isCancelled) {
          setIsPreviewLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      isCancelled = true;
    };
  }, [
    clientSignedApprovalLetterPreviewFileId,
    dispatch,
    hasApplicationFilesLoaded,
    isApplicationFilesLoading,
    setModalAlert,
    showGuaranteesRequestError,
  ]);

  useFreshGuaranteesRequestError(
    applicationError || submitApprovalLetterByAmManagerError,
    showGuaranteesRequestError
  );

  useEffect(() => {
    if (isAmAccessDenied) {
      setModalAlert({ text: MANAGER_APPROVAL_LETTER_MANUAL_SIGN_ACCESS_DENIED_TEXT });
    }
  }, [isAmAccessDenied, setModalAlert]);

  return {
    effectiveApplicationId,
    handleCloseAlertModal,
    handleCloseAmAccessModal,
    handleCloseSuccessModal,
    handleOpenPdf: pdfPreview.handleOpenPdf,
    handleSubmitByAmManager,
    headerProps,
    isAmAccessDenied,
    isAmAccessLoading,
    isApplicationLoading,
    isDocumentLoading,
    isSubmitDisabled,
    isSubmittingByAmManager,
    modalAlert,
    modalSuccess,
    pdfUrl: pdfPreview.pdfUrl,
    redirectPath,
    showGuaranteesRequestError,
  };
};
